import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountyPath,
    buildHotspotDetailPath,
    buildStatePath,
    getCountryLabel,
    getHotspotExplorerHeroImage,
    getHotspotExplorerStats,
    getStateProvinceLabel,
    getVisibleHotspots,
    sortHotspots
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";

export function CountyHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = '', stateProvince = '', county = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const stateKey = `${countryCode}|${stateProvince}`;
    const countyKey = `${countryCode}|${stateProvince}|${county}`;
    const hotspots = useMemo(() => hotspotExplorerIndex.hotspotsByCountyKey.get(countyKey) ?? [], [countyKey, hotspotExplorerIndex]);
    const regionBounds = hotspots[0]?.regionData?.subnational2?.bounds ?? undefined;
    const countyLabel = hotspots[0]?.countyLabel || county;
    const stateLabel = hotspots[0]?.stateLabel || getStateProvinceLabel(stateProvince);
    const visibleHotspots = useMemo(
        () => sortHotspots(getVisibleHotspots(hotspots, {countryCode, stateProvince, county}), sortMode),
        [countryCode, county, hotspots, sortMode, stateProvince]
    );
    const countyGroups = hotspotExplorerIndex.countyGroupsByStateKey.get(stateKey) ?? [];

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const openCounty = (nextCounty: string) => {
        navigate(`${buildCountyPath(countryCode, stateProvince, nextCounty)}${location.search}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'county', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={countyLabel}
            intro={`Hotspots you've visited in ${countyLabel}, ${stateLabel}.`}
            backLabel={`Back to ${stateLabel}`}
            onBack={() => navigate(`${buildStatePath(countryCode, stateProvince)}${location.search}`)}
            stats={getHotspotExplorerStats(visibleHotspots, 'Counties')}
            showChildStat={false}
            panelHeading="By County"
            groups={countyGroups}
            activeGroupCode={county}
            onSelectGroup={openCounty}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            onOpenHotspot={openHotspot}
        />
    );
}
